/**
 * Widget Selectors
 *
 * Selectors that derive values from multiple state slices for widget-level concerns.
 */

import type { AppState } from '@/domain/app';
import type { ConfigState } from '@/domain/configuration';
import { isUnreadAgentMessage, type Message, type MessagesState, type StreamingMessage } from '@/domain/message';
import { MESSAGE_SUB_TYPES } from '@/domain/messageEnums';
import type { PendingQuickReply } from '@/domain/quickReplies';
import type { UiState } from '@/domain/ui';

/**
 * True while the shopper has a conversation in flight: messages have loaded and
 * the session has not ended. V1's equivalent (`hasActiveSessionSelector`) reads
 * the chat provider's session directly; V2 does not keep the session in the
 * store, so it derives the same thing from the hydrated messages, which
 * startFullWidget fetches before the widget mounts, or runDeferredHydration
 * fetches once a late chat-provider connect arrives. This gates the Beacon
 * connection, so it must not read true before either has run.
 */
export function getHasActiveConversation({
  app,
  messages,
}: {
  app: Pick<AppState, 'isSessionEnded'>;
  messages: Pick<MessagesState, 'data'>;
}): boolean {
  return Object.keys(messages.data).length > 0 && !app.isSessionEnded;
}

export function getIsVisible({
  app,
  configuration,
}: {
  app: Pick<AppState, 'initialized'>;
  configuration: ConfigState;
}): boolean {
  if (!app.initialized) {
    return false;
  }

  if (!configuration.dynamic.enabled) {
    return false;
  }

  return true;
}

/**
 * Whether this page's display rules permit chat UI. Every deliberate open —
 * `Gladly.show()`, a launcher click — clears `suppressedByRules` first, so a
 * still-suppressed page is one nobody opened chat on *since the verdict was last
 * taken*: what a restored Panel display mode carries there is a stale preference,
 * not an open widget. The verdict is retaken per page and per route (see
 * evaluateDisplayRules), so an open with no conversation behind it lasts until the
 * next navigate and no further.
 */
export function getIsAllowedByDisplayRules({
  hasActiveConversation,
  ui,
}: {
  hasActiveConversation: boolean;
  ui: Pick<UiState, 'pinnedByConversation' | 'suppressedByRules'>;
}): boolean {
  // An in-flight conversation outranks the display rules, matching V1: rules
  // decide where a shopper can START a chat, not where they can keep one. Once
  // a conversation exists it follows them onto pages the rules suppress —
  // otherwise navigating off the matched page would strand a live chat with an
  // agent (or the AI) behind an invisible widget.
  if (hasActiveConversation) {
    return true;
  }

  // A conversation that ended on this page keeps chat on screen for the rest of the
  // page view, so it cannot be taken away mid-read. App only pins while an active
  // conversation is overriding the rules here, and pageLoaded clears it.
  if (ui.pinnedByConversation) {
    return true;
  }

  return !ui.suppressedByRules;
}

/**
 * Whether this page's rules ask the entry point to be the compact button rather than the
 * chat bar. Read by `WidgetDisplayer`, which passes it down instead of letting `Minimized`
 * ask again: the two tiers have to agree on the face for the whole minimized period, and
 * one value read once cannot disagree with itself.
 *
 * Unlike `getIsAllowedByDisplayRules`, an active conversation and a pin do not override
 * this. Those exist so a live chat is never stranded behind an invisible widget, which is
 * a question about reachability; which face a launcher that IS on screen wears stays the
 * page's to answer.
 */
export function getIsButtonOnlyByDisplayRules({ ui }: { ui: Pick<UiState, 'buttonOnlyByRules'> }): boolean {
  return ui.buttonOnlyByRules;
}

export function getHasUnreadAgentMessages({
  chatLastViewedAt,
  minimizedAt,
  messagesData,
}: {
  chatLastViewedAt: string | null;
  minimizedAt: string | null;
  messagesData: Record<string, Message>;
}): boolean {
  const cutoff = chatLastViewedAt ?? minimizedAt;
  if (cutoff === null) return false;
  // Early-exits on the first unread message; only getUnreadAgentMessages
  // pays for the full scan and sort.
  return Object.values(messagesData).some((message) => isUnreadAgentMessage(message, cutoff));
}

/**
 * Returns the non-user messages received after the read cutoff (last time the
 * chat was viewed, falling back to when it was minimized), newest first.
 * Empty when there is no cutoff or nothing is unread. The preview walks this
 * list to find the newest message it can render, so a non-previewable arrival
 * (e.g. an image) falls back to the previous previewable one.
 */
export function getUnreadAgentMessages({
  chatLastViewedAt,
  minimizedAt,
  messagesData,
}: {
  chatLastViewedAt: string | null;
  minimizedAt: string | null;
  messagesData: Record<string, Message>;
}): Message[] {
  const cutoff = chatLastViewedAt ?? minimizedAt;
  if (cutoff === null) return [];
  return Object.values(messagesData)
    .filter((message) => isUnreadAgentMessage(message, cutoff))
    .sort((a, b) => (a.date > b.date ? -1 : 1));
}

/**
 * The quick reply the consumer is currently being asked to answer, or null.
 *
 * A request counts as answered once a QUICK_REPLY_RESPONSE naming it exists —
 * including the optimistic one created on click, so the buttons clear the moment
 * a selection is made rather than when the server acknowledges it. Requests from
 * before the last session end are skipped: a new session should never open on a
 * stale question (V1's `getNextUnfilledQuickReplyRequest`).
 */
export function getUnansweredQuickReply({
  messagesData,
}: {
  messagesData: Record<string, Message>;
}): PendingQuickReply | null {
  const messages = Object.values(messagesData);

  const answeredRequestIds = new Set(
    messages
      .filter((message) => message.subType === MESSAGE_SUB_TYPES.QUICK_REPLY_RESPONSE)
      .map((message) => message.content.requestMessageId),
  );

  const sessionStartedAt = getLatestSessionEndDate(messages);

  const unanswered = messages
    .filter((message) => message.subType === MESSAGE_SUB_TYPES.QUICK_REPLY_REQUEST)
    .filter((message) => !answeredRequestIds.has(message.id))
    .filter((message) => sessionStartedAt === null || message.date > sessionStartedAt)
    // A request with no options renders no buttons, so it is skipped rather than
    // returned — that hands the footer back to the composer instead of stranding
    // the consumer. Filtered here rather than after picking the oldest, or a
    // malformed early request would mask a perfectly good later one.
    .filter((message) => message.content.options?.length)
    .sort((a, b) => (a.date > b.date ? 1 : -1));

  const next = unanswered[0];
  if (!next) {
    return null;
  }

  return { id: next.id, content: next.content };
}

function getLatestSessionEndDate(messages: Message[]): string | null {
  let latest: string | null = null;
  for (const message of messages) {
    if (message.subType === MESSAGE_SUB_TYPES.SESSION_END && (latest === null || message.date > latest)) {
      latest = message.date;
    }
  }
  return latest;
}

/**
 * The live streamed replies to render, as an array. State keys them by
 * `externalMessageId`; at most one is live at a time, but the array form lets
 * the render path generalize if concurrent streams ever arrive.
 */
export function getStreamingMessages({
  streamingMessages,
}: Pick<MessagesState, 'streamingMessages'>): StreamingMessage[] {
  return Object.values(streamingMessages);
}

/**
 * Assemble a streamed reply's visible text: order the additive fragments by
 * their turn-monotonic `sequence` (they are stored in network-arrival order,
 * which can differ) and concatenate. Pure — never mutates the stored deltas.
 */
export function getStreamingMessageText({ deltas }: Pick<StreamingMessage, 'deltas'>): string {
  return [...deltas]
    .sort((a, b) => a.sequence - b.sequence)
    .map((delta) => delta.text)
    .join('');
}
